import { create } from "zustand";
import { ApiStatusError, postHelpAgentMessage, type HelpAgentMessage } from "@/lib/api";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";

/** サーバーが受け付ける会話の最大件数と合計文字数。 */
const MAX_SENT_MESSAGES = 20;
const MAX_SENT_TEXT_LENGTH = 30_000;

interface HelpAgentState {
	isOpen: boolean;
	messages: HelpAgentMessage[];
	/** 入力中の質問。パネルを閉じても残し、送信に失敗したら送った質問を戻す。 */
	draft: string;
	pending: boolean;
	error: string | null;
	toggle: () => void;
	setDraft: (draft: string) => void;
	send: (text: string, currentPath: string) => Promise<void>;
	reset: () => void;
}

/** 直近の会話をサーバー上限まで切り詰める。先頭はユーザー発言でなければならない。 */
export function recentHistory(messages: HelpAgentMessage[]): HelpAgentMessage[] {
	let start = messages.length;
	let total = 0;
	while (
		start > 0 &&
		messages.length - start < MAX_SENT_MESSAGES &&
		total + messages[start - 1].text.length <= MAX_SENT_TEXT_LENGTH
	) {
		start--;
		total += messages[start].text.length;
	}
	const recent = messages.slice(start);
	const firstUser = recent.findIndex((m) => m.role === "user");
	return firstUser < 0 ? [] : recent.slice(firstUser);
}

export const useHelpAgentStore = create<HelpAgentState>((set, get) => ({
	isOpen: false,
	messages: [],
	draft: "",
	pending: false,
	error: null,
	toggle: () => set((s) => ({ isOpen: !s.isOpen })),
	setDraft: (draft) => set({ draft }),
	send: async (text, currentPath) => {
		const previous = get().messages;
		const messages: HelpAgentMessage[] = [...previous, { role: "user", text }];
		set({ messages, pending: true, error: null });
		try {
			const reply = await postHelpAgentMessage(recentHistory(messages), currentPath);
			set({ messages: [...messages, { role: "assistant", text: reply }] });
		} catch (err) {
			if (err instanceof ApiStatusError && err.status === 409)
				useCurrentUserStore.setState({ creditExhausted: true });
			set((s) => ({
				messages: previous,
				draft: s.draft === "" ? text : `${text}\n${s.draft}`,
				error: err instanceof Error ? err.message : "回答を取得できませんでした。",
			}));
			throw err;
		} finally {
			set({ pending: false });
		}
	},
	reset: () => set({ messages: [], error: null }),
}));
